import { useEffect, useRef, useState } from "react";
import {
  CircleNotch,
  CloudArrowUp,
  Eye,
  EyeSlash,
  Trash,
  FilePdf,
  Image as ImageIcon,
  FileDoc,
  FileText,
  ArrowSquareOut,
  Files,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const CATEGORIES = ["general", "policy", "marketing", "retailer-pack", "press"];

const formatBytes = (b) => {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const iconFor = (mime) => {
  if (!mime) return FileText;
  if (mime === "application/pdf") return FilePdf;
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime.includes("word")) return FileDoc;
  return FileText;
};

export default function AdminDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [label, setLabel] = useState("");
  const [category, setCategory] = useState("general");
  const [isPublic, setIsPublic] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/documents");
      setDocs(r.data.documents || []);
    } catch {
      toast.error("Could not load documents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!label.trim()) {
      toast.error("Add a label first, then pick a file");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("label", label.trim());
      fd.append("category", category);
      fd.append("is_public", String(isPublic));
      await api.post("/admin/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded "${label}"`);
      setLabel("");
      setCategory("general");
      setIsPublic(false);
      e.target.value = "";
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const togglePublic = async (doc) => {
    try {
      await api.patch(`/admin/documents/${doc.id}`, { is_public: !doc.is_public });
      toast.success(doc.is_public ? "Now private" : "Now public");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.label}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/documents/${doc.id}`);
      toast.success("Deleted");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const openDoc = async (doc) => {
    try {
      const r = await api.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoke after 60s — gives the browser time to render the file
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open document");
    }
  };

  return (
    <div data-testid="admin-documents-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold">
            Documents
          </p>
          <h1 className="font-display text-3xl sm:text-4xl mt-2 text-[#0A1128]">
            Document manager
          </h1>
          <p className="mt-2 text-sm text-[#4A5568] max-w-2xl">
            Upload PDFs, images, and policy attachments — up to 10 MB each. Mark a doc{" "}
            <strong className="text-[#0A1128]">public</strong> to expose it on{" "}
            <code className="px-1.5 py-0.5 bg-[#F5EFE6] rounded text-xs">/api/documents/public</code>.
          </p>
        </div>
      </div>

      {/* Upload form */}
      <div className="mt-6 mc-card p-6" data-testid="upload-card">
        <div className="flex items-center gap-2">
          <CloudArrowUp size={20} weight="duotone" className="text-[#3B5BDB]" />
          <h2 className="font-display text-xl text-[#0A1128]">Upload new document</h2>
        </div>
        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">
              Label *
            </label>
            <input
              data-testid="doc-label-input"
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Mine AI retailer pack v2"
              className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
            />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wider text-[#4A5568]">
              Category
            </label>
            <select
              data-testid="doc-category-select"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full h-11 px-3 rounded-md border border-[#E8DFD1] bg-white outline-none focus:border-[#3B5BDB]"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm text-[#0A1128] cursor-pointer">
            <input
              data-testid="doc-public-toggle"
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="h-4 w-4 accent-[#3B5BDB]"
            />
            Make publicly downloadable
          </label>
          <span className="text-xs text-[#4A5568]/80">PDF, PNG, JPG, WebP, DOC, DOCX, TXT, MD, CSV — max 10 MB</span>
        </div>
        <div className="mt-5">
          <input
            ref={fileRef}
            data-testid="doc-file-input"
            type="file"
            onChange={onFileChange}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.txt,.md,.csv"
            disabled={uploading}
            className="block w-full text-sm text-[#4A5568] file:mr-3 file:py-2.5 file:px-4 file:rounded-md file:border-0 file:bg-[#3B5BDB] file:text-white file:font-medium file:cursor-pointer hover:file:bg-[#2F49AF] file:smooth disabled:opacity-50"
          />
          {uploading && (
            <p className="mt-3 flex items-center gap-2 text-sm text-[#3B5BDB]">
              <CircleNotch size={14} className="animate-spin" weight="bold" /> Uploading...
            </p>
          )}
        </div>
      </div>

      {/* Documents list */}
      <div className="mt-8" data-testid="documents-list">
        <div className="flex items-center gap-2 mb-4">
          <Files size={18} weight="duotone" className="text-[#3B5BDB]" />
          <h2 className="font-display text-xl text-[#0A1128]">All documents</h2>
          <span className="text-xs text-[#4A5568]">({docs.length})</span>
        </div>

        {loading ? (
          <div className="mc-card p-10 flex items-center justify-center gap-3 text-[#4A5568]">
            <CircleNotch className="animate-spin" weight="bold" /> Loading documents...
          </div>
        ) : docs.length === 0 ? (
          <div className="mc-card p-10 text-center">
            <Files size={36} weight="duotone" className="text-[#3B5BDB]/40 mx-auto" />
            <p className="mt-3 font-display text-xl text-[#0A1128]">No documents yet</p>
            <p className="mt-1 text-sm text-[#4A5568]">Upload your first PDF or asset above.</p>
          </div>
        ) : (
          <div className="mc-card overflow-hidden">
            <ul className="divide-y divide-[#E8DFD1]">
              {docs.map((d) => {
                const Icon = iconFor(d.mime_type);
                return (
                  <li
                    key={d.id}
                    className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-3"
                    data-testid={`doc-${d.id}`}
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <Icon size={26} weight="duotone" className="text-[#3B5BDB] shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="font-medium text-[#0A1128] truncate">{d.label}</p>
                        <p className="text-xs text-[#4A5568] truncate">
                          {d.filename} · {d.mime_type} · {formatBytes(d.size_bytes)}
                        </p>
                        <p className="text-[11px] text-[#4A5568]/80 mt-0.5">
                          <span className="capitalize px-1.5 py-0.5 bg-[#F5EFE6] rounded">{d.category || "general"}</span>{" "}
                          · uploaded {new Date(d.created_at).toLocaleDateString()}{" "}
                          {d.uploaded_by ? `· by ${d.uploaded_by}` : ""}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        data-testid={`doc-toggle-public-${d.id}`}
                        onClick={() => togglePublic(d)}
                        className={`px-3 py-1.5 rounded-md text-xs font-medium smooth flex items-center gap-1.5 ${
                          d.is_public
                            ? "bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/30"
                            : "bg-[#F5EFE6] text-[#4A5568] border border-[#E8DFD1]"
                        }`}
                      >
                        {d.is_public ? (
                          <>
                            <Eye size={12} weight="bold" /> Public
                          </>
                        ) : (
                          <>
                            <EyeSlash size={12} weight="bold" /> Private
                          </>
                        )}
                      </button>
                      <button
                        data-testid={`doc-open-${d.id}`}
                        onClick={() => openDoc(d)}
                        className="p-2 text-[#3B5BDB] hover:bg-[#3B5BDB]/10 rounded smooth"
                        title="Open in new tab"
                      >
                        <ArrowSquareOut size={16} weight="duotone" />
                      </button>
                      <button
                        data-testid={`doc-delete-${d.id}`}
                        onClick={() => remove(d)}
                        className="p-2 text-[#4A5568] hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded smooth"
                        title="Delete"
                      >
                        <Trash size={16} weight="duotone" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
